/**
 * SpacerCutCheckboxCell Component
 * Checkbox component for marking orders as spacer cut in the spacer cutting workflow
 */

import React, { useState } from 'react';
import { Check } from 'lucide-react';
import { useUpdateOrderMutation } from '../../store/slices/apiSlice';
import { useToast } from '../../hooks/useToast';
import type { OrderSummary } from '../../utils/types';

interface SpacerCutCheckboxCellProps {
  order: OrderSummary;
  className?: string;
  onCutChange?: (orderId: string, cut: boolean) => void;
}

export const SpacerCutCheckboxCell: React.FC<SpacerCutCheckboxCellProps> = ({ 
  order, 
  className = '',
  onCutChange 
}) => {
  const [updateOrder, { isLoading }] = useUpdateOrderMutation();
  const toast = useToast();
  
  // Get current spacer cut status from metadata - matches dashboard logic
  const currentCut = order.metadata?.spacer_workflow?.done ?? false;
  const [isUpdating, setIsUpdating] = useState(false);

  const handleCutToggle = async () => {
    if (isLoading || isUpdating) return;
    
    setIsUpdating(true);
    const newCutStatus = !currentCut;
    
    try {
      // Update the metadata with spacer workflow status - matches dashboard
      const updatedMetadata = {
        ...order.metadata,
        spacer_workflow: {
          ...order.metadata?.spacer_workflow,
          done: newCutStatus,
          done_at: newCutStatus ? new Date().toISOString() : null,
          done_by: newCutStatus ? 'current_user' : null // This would be replaced with actual user info
        }
      };
      
      await updateOrder({ 
        id: order.id, 
        data: { metadata: updatedMetadata }
      }).unwrap();
      
      toast.success(
        newCutStatus ? 'Spacer Cut Complete' : 'Spacer Cut Unchecked', 
        newCutStatus 
          ? 'Order marked as spacer cut complete - shows ✅ on dashboard' 
          : 'Spacer cutting status removed from dashboard'
      );
      
      // Notify parent component of the change
      onCutChange?.(order.id, newCutStatus);
      
    } catch (e: any) {
      toast.error('Update failed', e?.data?.detail || 'Please try again.');
    } finally {
      setIsUpdating(false);
    }
  };

  const cellClasses = [
    'p-2 border',
    'text-center',
    className
  ].filter(Boolean).join(' ');

  return (
    <td className={cellClasses}>
      <div className="flex items-center justify-center">
        <div className="flex items-center">
          <label className="relative cursor-pointer group">
            <input
              type="checkbox"
              checked={currentCut}
              onChange={handleCutToggle}
              disabled={isLoading || isUpdating}
              className="sr-only"
            />
            <div 
              className={`
                w-5 h-5 border-2 rounded flex items-center justify-center transition-all duration-200
                ${currentCut 
                  ? 'bg-orange-500 border-orange-500' 
                  : 'border-gray-300 hover:border-orange-400 group-hover:bg-orange-50'
                }
                ${(isLoading || isUpdating) ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}
              `}
            >
              {currentCut && (
                <Check className="w-3 h-3 text-white" strokeWidth={3} />
              )}
            </div>
          </label>
          {(isLoading || isUpdating) && (
            <div className="ml-2 w-4 h-4 border-2 border-orange-300 border-t-orange-600 rounded-full animate-spin"></div>
          )}
        </div>
      </div>
    </td>
  );
};

SpacerCutCheckboxCell.displayName = 'SpacerCutCheckboxCell';
